import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { dynamicBaseQuery } from '../badRequestHandler';



// Create the auth API service
export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery:dynamicBaseQuery,
  tagTypes: ['Auth', 'User', 'Events', 'Messages'],
  endpoints: (builder) => ({
    // Login endpoint
    login: builder.mutation({
      query: (credentials) => ({
        url: '/api/auth/login',
        method: 'POST',
        body: credentials,
      }),
      
      invalidatesTags: ['Auth', 'User'],
    }),
    
    // Get current user profile endpoint
    getUserProfile: builder.query({
      query: () => ({
        url: '/api/auth/me',
        method: 'GET',
      }),
      providesTags: ['User'],
    }),
    
    // Get user info endpoint
    getUserInfo: builder.query({
      query: () => ({
        url: '/api/auth/user-info',
        method: 'GET',
      }),
      providesTags: ['User'],
    }),
    
    // Create user endpoint
    createUser: builder.mutation({
      query: (userData) => ({
        url: '/api/users/create',
        method: 'POST',
        body: userData,
      }),
      invalidatesTags: ['User'],
    }),
    
    // Get users by type endpoint
    getUsersByType: builder.query({
      query: ({ userType, search = '', page = 1, limit = 20 }) => ({
        url: `/api/downline/all-by-type?userType=${userType}${search ? `&search=${search}` : ''}&page=${page}&limit=${limit}`,
        method: 'GET',
      }),
      providesTags: ['User'],
    }),

    // Get user children endpoint
    getUserChildren: builder.query({
      query: (userType) => ({
        url: `/api/users/children${userType ? `?level=${userType}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['User'],
    }),

    // Get downline by type endpoint
    getDownlineByType: builder.query({
      query: (userType) => ({
        url: `/api/hierarchy/downline-by-type?userType=${userType}`,
        method: 'GET',
      }),
      providesTags: ['User'],
    }),

    // Get user by ID endpoint
    getUserById: builder.query({
      query: (id) => ({
        url: `/api/hierarchy/user/${id}`,
        method: 'GET',
      }),
      providesTags: ['User'],
    }),
    
    // Get downline by adminId endpoint
    getDownlineByAdminId: builder.query({
      query: ({ adminId, page = 1, limit = 20 }) => ({
        url: `/api/downline/direct?adminId=${adminId}&page=${page}&limit=${limit}`,
        method: 'GET',
      }),
      providesTags: ['User'],
    }),
    
    // Search downline by userId endpoint
    searchDownlineByUserId: builder.query({
      query: ({ userId, userType, beventId }) => ({
        url: `/api/downline/search?userId=${userId}${userType ? `&userType=${userType}` : ''}${beventId ? `&beventId=${beventId}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['User'],
    }),

    // Search users endpoint
    searchUsers: builder.query({
      query: (q) => ({
        url: `/api/search-users?q=${q}`,
        method: 'GET',
      }),
      providesTags: ['User'],
    }),
    
    // Get open events endpoint
    getOpenEvents: builder.query({
      query: () => ({
        url: '/api/events/live-api',
        method: 'GET',
      }),
      providesTags: ['Events'],
    }),
    
    // Get user balance endpoint
    getUserBalance: builder.query({
      query: (userId) => ({
        url: `/api/balance/${userId}`,
        method: 'GET',
      }),
      providesTags: ['User'],
    }),
    
    // Update user balance endpoint
    updateUserBalance: builder.mutation({
      query: ({ userId, amount, type }) => ({
        url: '/api/credit-debit',
        method: 'POST',
        body: { userId, amount, type },
      }),
      invalidatesTags: ['User'],
    }),
    
    // Get user transactions endpoint
    getUserTransactions: builder.query({
      query: ({ userId, page = 1, limit = 100 }) => ({
        url: `/api/balance/transactions/${userId}?page=${page}&limit=${limit}`,
        method: 'GET',
      }),
      providesTags: ['User'],
    }),
    
    // Get user details endpoint
    getUserDetails: builder.query({
      query: (userId) => ({
        url: `/api/users/${userId}/details`,
        method: 'GET',
      }),
      providesTags: ['User'],
    }),
    
    // Get inactive users by type endpoint
    getInactiveUsersByType: builder.query({
      query: ({ userType, search }) => {
        let url = `/api/downline/inactive-by-type?userType=${userType}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        return { url, method: 'GET' };
      },
      providesTags: ['User'],
    }),
    
    // Toggle user status endpoint
    toggleUserStatus: builder.mutation({
      query: (userId) => ({
        url: `/api/users/${userId}/toggle-status`,
        method: 'POST',
      }),
      invalidatesTags: ['User'],
    }),
    
    // Edit user endpoint
    editUser: builder.mutation({
      query: ({ userId, data }) => ({
        url: `/api/users/${userId}/edit`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),
    
    // Reset user password endpoint
    resetUserPassword: builder.mutation({
      query: ({ userId, newPassword }) => ({
        url: `/api/users/${userId}/reset-password`,
        method: 'PUT',
        body: { newPassword },
      }),
      invalidatesTags: ['User'],
    }),
    
    // Get login report endpoint
    getLoginReport: builder.query({
      query: ({ userId, page = 1, limit = 20, startDate, endDate }) => ({
        url: `/api/activity/login-report/${userId}?page=${page}&limit=${limit}&startDate=${startDate}&endDate=${endDate}`,
        method: 'GET',
      }),
      providesTags: ['User'],
    }),
    
    // Get activity logs endpoint
    getActivityLogs: builder.query({
      query: ({ userId, page = 1, limit = 100, startDate, endDate, activityType }) => {
        let url = `/api/activity/logs/${userId}?page=${page}&limit=${limit}`;
        if (startDate)    url += `&startDate=${startDate}`;
        if (endDate)      url += `&endDate=${endDate}`;
        if (activityType) url += `&activityType=${activityType}`;
        return { url, method: 'GET' };
      },
      providesTags: ['User'],
    }),
    
    // Get users for transaction endpoint
    getUsersForTransaction: builder.mutation({
      query: (userType) => ({
        url: '/user/username-id-search',
        method: 'POST',
        body: { userType },
      }),
    }),
    
    // Create cash transaction endpoint
    createCashTransaction: builder.mutation({
      query: (transactionData) => ({
        url: '/admin/api/cash-transaction',
        method: 'POST',
        body: transactionData,
      }),
      invalidatesTags: ['User'],
    }),
    
    // Get admin account statement endpoint
    getAdminAccountStatement: builder.query({
      query: () => ({
        url: '/api/admin/account-statement',
        method: 'GET',
      }),
      providesTags: ['User'],
    }),
    
    // Get user upline endpoint
    getUserUpline: builder.query({
      query: (userId) => ({
        url: `/api/users/${userId}/upline`,
        method: 'GET',
      }),
      providesTags: ['User'],
    }),
    
    // Get client account operations endpoint
    getClientAccountOperations: builder.query({
      query: () => ({
        url: '/api/admin/clientaccountoperations',
        method: 'GET',
      }),
      providesTags: ['User'],
    }),
    
    // Change password endpoint
    changePassword: builder.mutation({
      query: (passwordData) => ({
        url: '/api/auth/change-password',
        method: 'PUT',
        body: passwordData,
      }),
      invalidatesTags: ['Auth'],
    }),
    
    // Get active messages endpoint
    getActiveMessages: builder.query({
      query: () => ({
        url: '/api/messages/active',
        method: 'GET',
      }),
      providesTags: ['Messages'],
    }),
    
})
});

// Export hooks for usage in components
export const {
  useLoginMutation,
  useGetUserProfileQuery,
  useGetUserInfoQuery,
  useCreateUserMutation,
  useGetUsersByTypeQuery,
  useGetUserChildrenQuery,
  useGetDownlineByTypeQuery,
  useGetUserByIdQuery,
  useGetDownlineByAdminIdQuery,
  useSearchDownlineByUserIdQuery,
  useLazySearchDownlineByUserIdQuery,
  useSearchUsersQuery,
  useLazySearchUsersQuery,
  useGetOpenEventsQuery,
  useGetUserBalanceQuery,
  useUpdateUserBalanceMutation,
  useGetUserTransactionsQuery,
  useGetUserDetailsQuery,
  useGetInactiveUsersByTypeQuery,
  useToggleUserStatusMutation,
  useEditUserMutation,
  useResetUserPasswordMutation,
  useGetLoginReportQuery,
  useGetActivityLogsQuery,
  useGetUsersForTransactionMutation,
  useCreateCashTransactionMutation,
  useGetAdminAccountStatementQuery,
  useGetUserUplineQuery,
  useLazyGetUserUplineQuery,
  useGetClientAccountOperationsQuery,
  useChangePasswordMutation,
  useGetActiveMessagesQuery,
} = authApi;

// Export the reducer
export default authApi;